import { useCallback, useEffect, useRef, useState } from "react";

import type { CallScript } from "@frontierx/shared/schemas/CallScript";
import { CheckCircleIcon, Lightbulb, MessageCircle } from "lucide-react";
import { useOrganization } from "../Auth/UnifiedAuthProvider";

import { Button } from "@/components/ui/button";
import { SmoothScrollArea } from "@/components/ui/smooth-scroll-area";

import { useCallContext } from "../Call/CallContext";
import { PanelHeader } from "../PanelHeader";
import { CallScriptSelector } from "./CallScriptSelector";
import { PlaybookScriptItemNextPrev } from "./PlaybookScriptItemNextPrev";

export interface SmoothScrollAreaElement extends HTMLDivElement {
  smoothScrollTo: (options?: ScrollToOptions) => void;
}

export function PlaybookScriptDisplay() {
  const {
    mainSocket,
    roomId,
    handleCompleteScriptItem,
    handleSetCurrentScriptItem,
    playbook,
    currentScriptItem,
  } = useCallContext();
  const scrollAreaRef = useRef<SmoothScrollAreaElement>(null);
  const currentItemRef = useRef<HTMLDivElement>(null);

  const scrollToCurrentItem = useCallback(() => {
    if (currentItemRef.current && scrollAreaRef.current) {
      const scrollArea = scrollAreaRef.current;
      const currentItem = currentItemRef.current;

      const scrollAreaHeight = scrollArea.clientHeight;
      const itemTop = currentItem.offsetTop;
      const itemHeight = currentItem.clientHeight;

      // Calculate the scroll position to center the current item
      const scrollPosition = itemTop - scrollAreaHeight / 2 + itemHeight / 2;

      scrollArea.smoothScrollTo({ top: scrollPosition });
    }
  }, []);

  useEffect(() => {
    // Use a small timeout to ensure the DOM has updated
    const timeoutId = setTimeout(scrollToCurrentItem, 0);

    return () => clearTimeout(timeoutId);
  }, [scrollToCurrentItem]);

  useEffect(() => {
    // If there's no current script item, set it to the first item of the first section
    if (
      !currentScriptItem &&
      playbook.sections.length > 0 &&
      playbook.sections[0].scriptItems.length > 0
    ) {
      handleSetCurrentScriptItem(playbook.sections[0].scriptItems[0]);
    }
  }, [playbook, currentScriptItem, handleSetCurrentScriptItem]);

  const handlePrevious = useCallback(() => {
    const allScriptItems = playbook.sections.flatMap(
      (section) => section.scriptItems,
    );
    const currentIndex = allScriptItems.findIndex(
      (item) => item.id === currentScriptItem?.id,
    );
    if (currentIndex > 0) {
      handleSetCurrentScriptItem(allScriptItems[currentIndex - 1]);
    }
  }, [playbook, currentScriptItem, handleSetCurrentScriptItem]);

  const handleNext = useCallback(() => {
    const allScriptItems = playbook.sections.flatMap(
      (section) => section.scriptItems,
    );
    const currentIndex = allScriptItems.findIndex(
      (item) => item.id === currentScriptItem?.id,
    );
    if (currentIndex < allScriptItems.length - 1) {
      handleSetCurrentScriptItem(allScriptItems[currentIndex + 1]);
    }
  }, [playbook, currentScriptItem, handleSetCurrentScriptItem]);

  const currentSection = playbook?.sections?.find(
    (section) => section.id === playbook.currentSectionId,
  );

  const [selectedScript, setSelectedScript] = useState<CallScript | null>(null);
  const { organization } = useOrganization();
  const handleSwapScript = useCallback(
    (callScript: CallScript) => {
      setSelectedScript(callScript);
      mainSocket.send(
        JSON.stringify({
          type: "SWAP_SCRIPT",
          payload: {
            roomId,
            // #TODO we need to use JWTs for this
            orgId: organization?.id,
            scriptId: callScript.id,
          },
        }),
      );
    },
    [mainSocket, organization?.id, roomId],
  );
  return (
    <div className="flex h-full flex-col">
      <PanelHeader title={currentSection ? currentSection.name : "Loading…"}>
        <div className="flex w-full flex-row space-x-2">
          <div className="pt-1">
            <PlaybookScriptItemNextPrev
              onPrevious={handlePrevious}
              onNext={handleNext}
              onLocate={scrollToCurrentItem}
            />
          </div>
          <div>
            <CallScriptSelector
              size="sm"
              label="Switch script"
              mainSocket={mainSocket}
              selectedScript={selectedScript}
              onSelect={(callScript: CallScript) =>
                handleSwapScript(callScript)
              }
            />
          </div>
        </div>
      </PanelHeader>
      <div className="flex flex-grow flex-col overflow-hidden border-b border-gray-200">
        <SmoothScrollArea ref={scrollAreaRef} className="h-full" smoothScroll>
          <div>
            {playbook?.sections?.flatMap((section) =>
              section.scriptItems.map((item) => (
                <div
                  key={item.id}
                  ref={
                    item.id === currentScriptItem?.id ? currentItemRef : null
                  }
                  className={`border-b border-b-gray-100 p-2 ${
                    item.completed ? "text-green-500" : "text-gray-900"
                  } ${
                    item.id === currentScriptItem?.id
                      ? "bg-yellow-100"
                      : "hover:bg-yellow-50"
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <div className="flex-shrink-0">
                      <Button
                        variant={"ghost"}
                        className="px-2"
                        onClick={() => {
                          handleCompleteScriptItem(item);
                        }}
                      >
                        <ScriptItemIcon item={item} />
                      </Button>
                    </div>
                    <div className="pr-4">
                      <div className="font-medium">
                        <button
                          type="button"
                          onClick={() => handleSetCurrentScriptItem(item)}
                          className="inline text-left text-gray-700 hover:text-black"
                        >
                          {item.text}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )),
            )}
          </div>
        </SmoothScrollArea>
      </div>
    </div>
  );
}

const ScriptItemIcon = ({
  item,
}: {
  item: { completed: boolean; variant?: string };
}) => {
  if (item.completed) return <CheckCircleIcon size={16} />;
  if (item.variant === "drill_down") return <Lightbulb size={16} />;
  return <MessageCircle size={16} />;
};
